/**
 * Device capability check for the AI-proctored exam.
 *
 * The exam is desktop/laptop only: it depends on the Fullscreen API, tab-switch
 * and focus detection, and a stable webcam framing. None of those behave
 * reliably on a touch device, so we block rather than let a student burn an
 * attempt on a session that cannot be proctored.
 *
 * The signal is INPUT MODALITY, not viewport width. A desktop user with a
 * narrow window can still sit the exam, so width would block the wrong people.
 * Modality also survives Chrome's "Request Desktop Site", which rewrites the
 * user agent but cannot change the fact that the device has a touchscreen.
 */

/** iPadOS 13+ reports platform 'MacIntel' and, with a trackpad attached, even
 *  reports a fine pointer with hover. A real Mac reports maxTouchPoints 0. */
const isIPadOSMasqueradingAsMac = () =>
  navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform || '')

export const isExamCapableDevice = () => {
  if (typeof window === 'undefined') return true

  if (!window.matchMedia) {
    // Very old browser: fall back to a user-agent sniff rather than guessing.
    return !/Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent || '')
  }

  // Phones and touch-only tablets: coarse pointer with no hover.
  if (window.matchMedia('(pointer: coarse) and (hover: none)').matches) return false

  if (isIPadOSMasqueradingAsMac()) return false

  return true
}

/** Re-run the check when the input situation changes (e.g. a mouse is plugged
 *  in), so a user is never stranded on the gate. Returns an unsubscribe fn. */
export const onDeviceCapabilityChange = (handler) => {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}
  const query = window.matchMedia('(pointer: coarse) and (hover: none)')
  const listener = () => handler(isExamCapableDevice())
  if (query.addEventListener) {
    query.addEventListener('change', listener)
    return () => query.removeEventListener('change', listener)
  }
  query.addListener(listener)
  return () => query.removeListener(listener)
}
